import { nanoid } from "nanoid";
/**
 * Build a snapshot for a revert entry. Used by the fork-resolution
 * rollback path: each original that needs to be rolled back gets one
 * revert entry appended to local history.
 *
 * Currently for debugging only. The revert entry records that a
 * rollback happened (visible in the history viewer) but its patches
 * are not separately applied to the document — the rollback's state
 * mutation happens via an aggregate inverse-patch apply in
 * `rollbackLocalEntries`. The history scrubber does not currently
 * support traversing local history that contains revert entries.
 *
 * Records are reversed; within each record the `patches` and
 * `inversePatches` arrays are swapped AND each is reversed. This
 * matches the way `TreePatchRecord.getPatches(Undo)` builds its patch
 * list (records reversed, inversePatches reversed) so the revert's
 * forward-apply would mirror the original's undo-apply if it were
 * applied directly.
 *
 * Known limitation: the inverse patches on the revert are the
 * original's forward patches (reversed). This is correct only while
 * scope-disjoint is the merge rule — no other entry can have modified
 * the paths between the original's apply and the rollback. To support
 * more fine-grained merges, these revert entries could be built using
 * live MST recording. See phase 2 of
 * docs/superpowers/specs/2026-04-24-fork-detection-rollback-recording-design.md.
 */
export function buildRevertEntrySnapshot(original, triggeringBatchIds) {
    var _a;
    const originalRecords = (_a = original.records) !== null && _a !== void 0 ? _a : [];
    const revertRecords = [...originalRecords]
        .reverse()
        .map(record => {
        var _a, _b;
        return ({
            tree: record.tree,
            action: record.action,
            patches: [...((_a = record.inversePatches) !== null && _a !== void 0 ? _a : [])].reverse(),
            inversePatches: [...((_b = record.patches) !== null && _b !== void 0 ? _b : [])].reverse(),
        });
    });
    return {
        id: nanoid(),
        tree: original.tree,
        model: original.model,
        action: original.action,
        undoable: false,
        state: "complete",
        records: revertRecords,
        isRevert: true,
        revertsEntryId: original.id,
        triggeringBatchIds,
    };
}
//# sourceMappingURL=data:application/json;base64,eyJ2ZXJzaW9uIjozLCJmaWxlIjoicmV2ZXJ0LWVudHJ5LmpzIiwic291cmNlUm9vdCI6IiIsInNvdXJjZXMiOlsiLi4vLi4vLi4vLi4vc3JjL21vZGVscy9oaXN0b3J5L3JldmVydC1lbnRyeS50cyJdLCJuYW1lcyI6W10sIm1hcHBpbmdzIjoiQUFBQSxPQUFPLEVBQUUsTUFBTSxFQUFFLE1BQU0sUUFBUSxDQUFDO0FBR2hDOzs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7Ozs7OztHQTBCRztBQUNILE1BQU0sVUFBVSx3QkFBd0IsQ0FDdEMsUUFBOEIsRUFDOUIsa0JBQTRCOztJQUU1QixNQUFNLGVBQWUsR0FBRyxNQUFBLFFBQVEsQ0FBQyxPQUFPLG1DQUFJLEVBQUUsQ0FBQztJQUMvQyxNQUFNLGFBQWEsR0FBOEIsQ0FBQyxHQUFHLGVBQWUsQ0FBQztTQUNsRSxPQUFPLEVBQUU7U0FDVCxHQUFHLENBQUMsTUFBTSxDQUFDLEVBQUU7O1FBQUMsT0FBQSxDQUFDO1lBQ2QsSUFBSSxFQUFFLE1BQU0sQ0FBQyxJQUFJO1lBQ2pCLE1BQU0sRUFBRSxNQUFNLENBQUMsTUFBTTtZQUNyQixPQUFPLEVBQUUsQ0FBQyxHQUFHLENBQUMsTUFBQSxNQUFNLENBQUMsY0FBYyxtQ0FBSSxFQUFFLENBQUMsQ0FBQyxDQUFDLE9BQU8sRUFBRTtZQUNyRCxjQUFjLEVBQUUsQ0FBQyxHQUFHLENBQUMsTUFBQSxNQUFNLENBQUMsT0FBTyxtQ0FBSSxFQUFFLENBQUMsQ0FBQyxDQUFDLE9BQU8sRUFBRTtTQUN0RCxDQUFDLENBQUE7S0FBQSxDQUFDLENBQUM7SUFFTixPQUFPO1FBQ0wsRUFBRSxFQUFFLE1BQU0sRUFBRTtRQUNaLElBQUksRUFBRSxRQUFRLENBQUMsSUFBSTtRQUNuQixLQUFLLEVBQUUsUUFBUSxDQUFDLEtBQUs7UUFDckIsTUFBTSxFQUFFLFFBQVEsQ0FBQyxNQUFNO1FBQ3ZCLFFBQVEsRUFBRSxLQUFLO1FBQ2YsS0FBSyxFQUFFLFVBQVU7UUFDakIsT0FBTyxFQUFFLGFBQWE7UUFDdEIsUUFBUSxFQUFFLElBQUk7UUFDZCxjQUFjLEVBQUUsUUFBUSxDQUFDLEVBQUU7UUFDM0Isa0JBQWtCO0tBQ25CLENBQUM7QUFDSixDQUFDIn0=